import SystemSolverTool from "../MathTools/SystemSolverTool";
import QuadraticGraphTool from "../MathTools/QuadraticGraphTool";
import CoordinatePlane from "../MathTools/CoordinatePlane";
import RationalGraphTool from "../MathTools/RationalGraphTool";
import PolygonTool from "../MathTools/PolygonTool";
import CoordinateCircle from "../MathTools/CoordinateCircle";


export function getQuestionPool(topic) {
    switch (topic) {
      /* -----------------------------------------
         CH 1: logicalReasoning
      ----------------------------------------- */
      case "logicalReasoning":
    return [
      {
        question: () => {
          const qHtml = `
            <p><strong>Scenario:</strong> "All dogs have four legs. My pet is a cat with four legs.
            Does that mean my pet is a dog?"</p>
            <p><strong>Question:</strong> Is the statement logically valid to conclude the pet is a dog?</p>
          `;
          return {
            q: qHtml,
            a: "No, it's invalid; having 4 legs doesn't imply dog",
            distract: ["Yes, it must be a dog", "Insufficient data to decide"]
          };
        }
      },
      {
        question: () => {
          const qHtml = `
            <p><strong>Scenario:</strong> A statement says "If x is even, then x^2 is even."</p>
            <p><strong>Question:</strong> Is there a valid counterexample? 
            (Any even integer that yields an odd square?)</p>
          `;
          return {
            q: qHtml,
            a: "No, there's no counterexample. It's always true for even x.",
            distract: ["Yes, x=2 is a counterexample", "Yes, x=4 is a counterexample"]
          };
        }
      },
      // 3rd question
      {
        question: () => {
          const qHtml = `
            <p><strong>Scenario:</strong> "If it rains, then the streets are wet. 
            The streets are not wet. Therefore, it's not raining."</p>
            <p><strong>Question:</strong> Is this reasoning valid or not?</p>
          `;
          return {
            q: qHtml,
            a: "Yes, it's valid. (Modus tollens: if P→Q, not Q => not P)",
            distract: [
              "No, it's a fallacy",
              "Insufficient data to conclude"
            ]
          };
        }
      },
      // 4th question
      {
        question: () => {
          const qHtml = `
            <p><strong>Scenario:</strong> "If a shape is a square, then it has four right angles.
            The shape in question has four right angles. Therefore, it must be a square."</p>
            <p><strong>Question:</strong> Which logical error applies here?</p>
          `;
          return {
            q: qHtml,
            a: "Affirming the consequent (incorrectly concluding a shape is a square)",
            distract: [
              "Denying the antecedent",
              "No error; it's valid reasoning"
            ]
          };
        }
      }
    ];
  
      /* -----------------------------------------
         CH 2: trigonometry
      ----------------------------------------- */
      case "trigonometry":
    return [
      // 1) [existing question about 210° reference angle]
      {
        illustration: (
          <CoordinatePlane
            width={300}
            height={300}
            showAxes
            lines={[
              // positive x-axis
              { start: [0, 0], end: [10, 0], color: "black", label: "0°" },
              // the 210° ray
              {
                start: [0, 0],
                end: [
                  10 * Math.cos((210 * Math.PI) / 180),
                  10 * Math.sin((210 * Math.PI) / 180)
                ],
                color: "red",
                label: "210°"
              }
            ]}
            style={{ margin: "1em auto", display: "block" }}
          />
        ),
        question: () => {
          const angle = 210, refAngle = 30;
          return {
            q: `
              <p><strong>Scenario:</strong> An angle of ${angle}° in standard position.</p>
              <p><strong>Question:</strong> What's the reference angle?</p>
            `,
            a: `${refAngle}°`,
            distract: [`${360 - angle}°`, `180°`]
          };
        }
      },
  
      // 2) [existing question about triangle ABC with Sine Law]
      {
        illustration: (
          <PolygonTool
            initialPoints={[
              { x: 50,   y: 150 },
              { x: 145.2, y:  70.2 },
              { x: 200,  y: 150 }
            ]}
            labels={["A","B","C"]}
            draggable={false}
            showStats={false}
          />
        ),
        question: () => {
          const a = 8, b = 10, Adeg = 40;
          return {
            q: `
              <p><strong>Scenario:</strong> In triangle ABC, side a = BC = ${a} cm,
              side b = AC = ${b} cm, angle A = ${Adeg}°.</p>
              <p><strong>Question:</strong> Using the Sine Law, find angle B (approx)?</p>
            `,
            a: "56° (approx)",
            distract: ["40°", "82°"]
          };
        }
      },
  
      // 3) NEW: sin(300°) with a unit circle illustration
      {
        illustration: (
          <CoordinateCircle
            width={300}
            height={300}
            circleRadius={4}
            circleColor="blue"
            showAxes={true}
            disablePlot={true}
            lines={[
              // a ray at 300°
              {
                start: [0, 0],
                end: [
                  4 * Math.cos((300 * Math.PI) / 180),
                  4 * Math.sin((300 * Math.PI) / 180)
                ],
                color: "red",
                label: "300°"
              }
            ]}
            style={{ margin: "1em auto", display: "block" }}
          />
        ),
        question: () => {
          return {
            q: `
              <p><strong>Scenario:</strong> An angle of 300° in standard position on the unit circle 
              (but radius 4 here for illustration).</p>
              <p><strong>Question:</strong> What is sin(300°)?</p>
            `,
            a: "-√3/2",
            distract: ["√3/2", "-1/2", "1/2"]
          };
        }
      },
  
      // 4) NEW: Cosine Law question with a polygon
      {
        illustration: (
          <PolygonTool
            initialPoints={[
              { x:  40, y: 160 },  // A
              { x: 220, y: 160 },  // B
              { x: 160, y:  80 }   // C
            ]}
            labels={["A","B","C"]}
            draggable={false}
            showStats={false}
          />
        ),
        question: () => {
          // Suppose side c=9 (AB=9), side a=7 (BC=7), angle B=55°, find side b=AC
          // Cosine Law: b^2 = a^2 + c^2 - 2ac cos(B)
          // => b^2 = 7^2 + 9^2 - 2*7*9*cos(55°) => ...
          // approximate
          const approxB = Math.sqrt(49 + 81 - 126*Math.cos(55*Math.PI/180)); 
          const bVal = approxB.toFixed(2); // e.g. "7.90"
          return {
            q: `
              <p><strong>Scenario:</strong> Triangle ABC, side AB=9, BC=7, angle B=55°.
              Using the Cosine Law to find AC.</p>
              <p><strong>Question:</strong> What is side AC (approx)?</p>
            `,
            a: `${bVal}`,
            distract: [ (bVal - 1).toFixed(2), (Number(bVal)+1).toFixed(2), "15.00" ]
          };
        }
      },
  
      // 5) NEW: Quadrant sign question using CoordinatePlane
      {
        illustration: (
          <CoordinatePlane
            width={300}
            height={300}
            showAxes={true}
            lines={[
              // a line in quadrant II, e.g. 135°
              {
                start: [0, 0],
                end: [
                  10 * Math.cos((135 * Math.PI) / 180),
                  10 * Math.sin((135 * Math.PI) / 180)
                ],
                color: "green",
                label: "135°"
              }
            ]}
            style={{ margin: "1em auto", display: "block" }}
          />
        ),
        question: () => {
          return {
            q: `
              <p><strong>Scenario:</strong> An angle of 135° in standard position, 
              lying in Quadrant II.</p>
              <p><strong>Question:</strong> In Quadrant II, which trig functions are positive?</p>
            `,
            a: "Sine only (sin>0, cos<0, tan<0)",
            distract: [
              "Cosine only",
              "All are positive",
              "Tangent only"
            ]
          };
        }
      }
    ];
  
  
      /* -----------------------------------------
         CH 3: quadratics
      ----------------------------------------- */
      case "quadratics":
    return [
      // 1) [Existing: x² - 3x -10]
      {
        illustration: (
          <QuadraticGraphTool
            equation="x^2 - 3x - 10"
            showRoots={true}
            showVertex={true}
            showAxisOfSymmetry={true}
            style={{ width: "100%", maxWidth: 400, margin: "1em auto" }}
          />
        ),
        question: () => {
          const qHtml = `
            <p><strong>Scenario:</strong> Solve the quadratic equation: x^2 - 3x - 10=0.</p>
          `;
          return {
            q: qHtml,
            a: "x=5 or x=-2",
            distract: ["x=2 or x=-5", "No real solutions"]
          };
        }
      },
  
      // 2) [Existing: -2(x-1)²+6 => vertex question]
      {
        illustration: (
          <QuadraticGraphTool
            equation="-2*(x-1)^2 + 6"
            showRoots={true}
            showVertex={true}
            showAxisOfSymmetry={true}
            style={{ width: "100%", maxWidth: 400, margin: "1em auto" }}
          />
        ),
        question: () => {
          const qHtml = `
            <p><strong>Scenario:</strong> The parabola y=-2(x-1)^2+6. 
            <strong>Question:</strong> Where is its vertex?</p>
          `;
          return {
            q: qHtml,
            a: "(1, 6)",
            distract: ["(-1, 6)", "(1, -6)"]
          };
        }
      },
  
      // 3) NEW: No real solutions example
      {
        illustration: (
          <QuadraticGraphTool
            equation="x^2 + 4*x + 8"
            showRoots={true}
            showVertex={true}
            showAxisOfSymmetry={true}
            style={{ width: "100%", maxWidth: 400, margin: "1em auto" }}
          />
        ),
        question: () => {
          const qHtml = `
            <p><strong>Scenario:</strong> Consider y = x^2 + 4x + 8. 
            <strong>Question:</strong> Does this quadratic have real roots?</p>
          `;
          return {
            q: qHtml,
            a: "No real solutions (discriminant < 0)",
            distract: ["Yes, two real solutions", "Yes, exactly one real solution"]
          };
        }
      },
  
      // 4) NEW: Factoring a simple quadratic
      {
        illustration: (
          <QuadraticGraphTool
            equation="x^2 - x - 6"
            showRoots={true}
            showVertex={false}
            style={{ width: "100%", maxWidth: 400, margin: "1em auto" }}
          />
        ),
        question: () => {
          const qHtml = `
            <p><strong>Scenario:</strong> Factor the quadratic x^2 - x - 6, then find its roots.</p>
          `;
          return {
            q: qHtml,
            a: "x=3 or x=-2",
            distract: ["x=2 or x=-3", "No real roots"]
          };
        }
      },
  
      // 5) NEW: Vertex form & max/min question
      {
        illustration: (
          <QuadraticGraphTool
            equation="2*(x+2)^2 - 5"
            showRoots={true}
            showVertex={true}
            style={{ width: "100%", maxWidth: 400, margin: "1em auto" }}
          />
        ),
        question: () => {
          const qHtml = `
            <p><strong>Scenario:</strong> The parabola y = 2(x+2)^2 - 5. 
            <strong>Question:</strong> Does it have a minimum or maximum, and where?</p>
          `;
          return {
            q: qHtml,
            a: "Minimum at x=-2, y=-5",
            distract: [
              "Maximum at x=-2, y=-5",
              "Minimum at x=2, y=-5",
              "No min/max (it's a line)"
            ]
          };
        }
      }
    ];
  
  
      /* -----------------------------------------
         CH 4: rationalExpressions
      ----------------------------------------- */
      case "rationalExpressions":
    return [
      // 1) Simplify
      {
          illustration: null,
          question: () => {
            const qHtml = `
              <p><strong>Scenario:</strong> Simplify the rational expression 
              <code>(x^2 - 9)/(x + 3)</code>, where <code>x ≠ -3</code>.</p>
            `;
            return {
              q: qHtml,
              a: "x - 3",
              distract: ["x + 3", "x - 9"]
            };
          }
        },
  
      // 2) Solve equation
      {
        // No illustration needed
        illustration: null,
        question: () => {
          const qHtml = `
            <p><strong>Scenario:</strong> Solve the rational equation 
            <code>x/(x-2) = 3</code>, where <code>x ≠ 2</code>.</p>
            <p>Multiply both sides by <code>(x - 2)</code> (watch domain restrictions) 
            to get a linear equation.</p>
            <pre style="background:#f5f5f5;padding:6px;">
            </pre>
          `;
          return {
            q: qHtml,
            a: "x=3",
            distract: ["x=6", "No solution"]
          };
        }
      },
  
      // 3) Domain & non-permissible
      {
        // No illustration needed
        illustration: null,
        question: () => {
          const qHtml = `
            <p><strong>Scenario:</strong> Consider the expression 
            <code>(2x + 1)/(x^2 + 2x - 3)</code>.</p>
            <p>Factor the denominator: <code>x^2 + 2x - 3 = (x+3)(x-1)</code>.</p>
            <p><strong>Question:</strong> Which x-values must be excluded from its domain?</p>
            <p style="font-style:italic;">
              (Hint: set each factor in the denominator to 0 
              and exclude those x-values.)
            </p>
          `;
          return {
            q: qHtml,
            a: "x=1 or x=-3",
            distract: ["No restrictions", "x=-1 or x=3", "x=0"]
          };
        }
      },
  
      // 4) Partial fractions
      {
        // No illustration needed
        illustration: null,
        question: () => {
          const qHtml = `
            <p><strong>Scenario:</strong> Decompose 
            <code>(2x + 1)/(x^2 + 3x + 2)</code> into partial fractions.</p>
            <p>Factor: <code>x^2 + 3x + 2 = (x+1)(x+2)</code>.</p>
            <p><strong>Question:</strong> Which form is correct?</p>
            <pre style="background:#f5f5f5;padding:6px;">
  (2x + 1) / [(x+1)(x+2)] 
  = A/(x+1) + B/(x+2).
            </pre>
          `;
          return {
            q: qHtml,
            a: `
              <p>
                <code>(2x + 1)/((x+1)(x+2)) = 1/(x+1) + 1/(x+2)</code>
              </p>
            `,
            distract: [
              `<p><code>(2x+1)/((x+1)(x+2)) = (2)/(x+1) + (1)/(x+2)</code></p>`,
              `<p><code>(2x+1)/((x+1)(x+2)) = (1)/(x+1) - (1)/(x+2)</code></p>`,
              `<p><code>(2x+1)/((x+1)(x+2)) = (2x+1)/(x+1) + (2x+1)/(x+2)</code></p>`
            ]
          };
        }
      },
  
      // 5) Graph & vertical asymptote with RationalGraphTool
      {
        // This question DOES need an illustration
        illustration: (
          <RationalGraphTool
            equation="1/(x-2)"
            width={400}
            height={300}
            xRange={[-6, 6]}
            yRange={[-6, 6]}
          />
        ),
        question: () => {
          const qHtml = `
            <p><strong>Scenario:</strong> The function <code>y = 1/(x - 2)</code> 
            is shown in the graph below. Notice its vertical asymptote that
            divides the plane into two branches.</p>
            <p><strong>Question:</strong> What is the equation of that vertical asymptote?</p>
          `;
          return {
            q: qHtml,
            a: "x = 2",
            distract: ["y = 2", "x = -2", "y = -2"]
          };
        }
      }
    ];
  
  
  
      /* -----------------------------------------
         CH 5: systemsEq
      ----------------------------------------- */
      case "systemsEq":
    return [
      {
        question: () => ({
          q: (
            <div>
              <p><strong>Scenario:</strong> Solve the linear system:</p>
              <p><em>2x + 3y = 7</em> and <em>x - y = 4</em>.</p>
              <div style={{ margin: "16px 0" }}>
                <SystemSolverTool
                  initA1={2} initB1={3} initC1={7}
                  initA2={1} initB2={-1} initC2={4}
                  showGraph={true}
                  showSolution={false}
                  showInputs={false}
                  width={300}
                  height={300}
                  padding={30}
                />
              </div>
              <p>Observe the intersection of these lines in the graph above.</p>
              <strong>Question:</strong> What is the single solution to this system?
            </div>
          ),
          a: "(3.8, -0.2)",
          distract: ["(1.6, 2.4)", "(3.3, 1.8)"]
        })
      },
      {
        question: () => ({
          q: (
            <div>
              <p><strong>Scenario:</strong> Solve the linear system:</p>
              <p><em>x + 2y = 8</em> and <em>2x - y = 1</em>.</p>
              <div style={{ margin: "16px 0" }}>
                <SystemSolverTool
                  initA1={1} initB1={2} initC1={8}
                  initA2={2} initB2={-1} initC2={1}
                  showGraph={true}
                  showSolution={false}
                  showInputs={false}
                />
              </div>
              <p>Each line is drawn above; determine their intersection point.</p>
              <strong>Question:</strong> What is the solution to this system?
            </div>
          ),
          a: "(2, 3)",
          distract: ["(4, 2)", "(1, 4)"]
        })
      },
      {
        question: () => ({
          q: (
            <div>
              <p><strong>Scenario:</strong> Consider the system:</p>
              <p><em>3x + 2y = 12</em> and <em>6x + 4y = 24</em>.</p>
              <div style={{ margin: "16px 0" }}>
                <SystemSolverTool
                  initA1={3} initB1={2} initC1={12}
                  initA2={6} initB2={4} initC2={24}
                  showGraph={true}
                  showSolution={false}
                  showInputs={false}
                />
              </div>
              <p>
                Notice that the second equation is a multiple of the first, so the
                lines may overlap entirely.
              </p>
              <strong>Question:</strong> How many solutions does this system have?
            </div>
          ),
          a: "Infinitely many solutions",
          distract: ["No solutions", "Exactly one solution"]
        })
      },
      {
        question: () => ({
          q: (
            <div>
              <p><strong>Scenario:</strong> Solve the linear system:</p>
              <p><em>2x - y = 0</em> and <em>x + 3y = 12</em>.</p>
              <div style={{ margin: "16px 0" }}>
                <SystemSolverTool
                  initA1={2}
                  initB1={-1}
                  initC1={0}
                  initA2={1}
                  initB2={3}
                  initC2={12}
                  showGraph={true}
                  showSolution={false}
                  showInputs={false}
                />
              </div>
              <p>Observe the two lines and find their unique intersection.</p>
              <strong>Question:</strong> What is the solution?
            </div>
          ),
          a: "(12/7, 24/7)",
          distract: ["(6, 6)", "(2, 4)"]
        })
      },
      {
        question: () => ({
          q: (
            <div>
              <p><strong>Scenario:</strong> Solve the system:</p>
              <p><em>3x - 4y = 5</em> and <em>6x - 8y = 10</em>.</p>
              <div style={{ margin: "16px 0" }}>
                <SystemSolverTool
                  initA1={3}
                  initB1={-4}
                  initC1={5}
                  initA2={6}
                  initB2={-8}
                  initC2={10}
                  showGraph={true}
                  showSolution={false}
                  showInputs={false}
                />
              </div>
              <p>One equation might be a multiple of the other, indicating an overlap.</p>
              <strong>Question:</strong> How many solutions does this system have?
            </div>
          ),
          a: "Infinitely many solutions",
          distract: ["No solutions", "Exactly one solution"]
        })
      },
      {
        question: () => ({
          q: (
            <div>
              <p>
                <strong>Scenario:</strong> Consider the lines{" "}
                <em>2x + y = 4</em> and <em>2x + y = 5</em>.
              </p>
              <div style={{ margin: "16px 0" }}>
                <SystemSolverTool
                  initA1={2}
                  initB1={1}
                  initC1={4}
                  initA2={2}
                  initB2={1}
                  initC2={5}
                  showGraph={true}
                  showSolution={false}
                  showInputs={false}
                />
              </div>
              <p>They have the same slope but different intercepts, meaning they never meet.</p>
              <strong>Question:</strong> How many solutions does this system have?
            </div>
          ),
          a: "No solutions",
          distract: ["Exactly one solution", "Infinitely many solutions"]
        })
      },
      {
        question: () => ({
          q: (
            <div>
              <p><strong>Scenario:</strong> Solve the system:</p>
              <p><em>x - y = 2</em> and <em>2x - 2y = 4</em>.</p>
              <div style={{ margin: "16px 0" }}>
                <SystemSolverTool
                  initA1={1}
                  initB1={-1}
                  initC1={2}
                  initA2={2}
                  initB2={-2}
                  initC2={4}
                  showGraph={true}
                  showInputs={false}
                  showSolution={false}
                />
              </div>
              <p>
                The second equation looks like twice the first—do they overlap completely?
              </p>
              <strong>Question:</strong> How many solutions does this system have?
            </div>
          ),
          a: "Infinitely many solutions",
          distract: ["No solutions", "Exactly one solution"]
        })
      },
      {
        question: () => ({
          q: (
            <div>
              <p><strong>Scenario:</strong> Solve the system:</p>
              <p><em>x + 3y = 5</em> and <em>2x + 6y = 12</em>.</p>
              <div style={{ margin: "16px 0" }}>
                <SystemSolverTool
                  initA1={1}
                  initB1={3}
                  initC1={5}
                  initA2={2}
                  initB2={6}
                  initC2={12}
                  showGraph={true}
                  showSolution={false}
                  showInputs={false}
                />
              </div>
              <p>
                Check if the second is a multiple of the first or if they intersect in
                one point.
              </p>
              <strong>Question:</strong> What is the solution?
            </div>
          ),
          // By manual calculation => no solutions (parallel lines)
          a: "No solutions",
          distract: ["(1, 4/3)", "Infinitely many solutions"]
        })
      },
      {
        question: () => ({
          q: (
            <div>
              <p><strong>Scenario:</strong> Another pair:</p>
              <p><em>3x + 9y = 15</em> and <em>6x + 18y = 30</em>.</p>
              <div style={{ margin: "16px 0" }}>
                <SystemSolverTool
                  initA1={3}
                  initB1={9}
                  initC1={15}
                  initA2={6}
                  initB2={18}
                  initC2={30}
                  showGraph={true}
                  showSolution={false}
                />
              </div>
              <p>Are these lines distinct or do they coincide?</p>
              <strong>Question:</strong> How many solutions does this system have?
            </div>
          ),
          // Multiply eqn1 by 2 => eqn2 => infinite solutions
          a: "Infinitely many solutions",
          distract: ["No solutions", "Exactly one solution"]
        })
      },
      {
        question: () => ({
          q: (
            <div>
              <p>
                <strong>Scenario:</strong> Consider the lines <em>x + y = 1</em> and{" "}
                <em>x + y = 3</em>.
              </p>
              <div style={{ margin: "16px 0" }}>
                <SystemSolverTool
                  initA1={1}
                  initB1={1}
                  initC1={1}
                  initA2={1}
                  initB2={1}
                  initC2={3}
                  showGraph={true}
                  showSolution={false}
                  showInput={false}
                />
              </div>
              <p>They appear to be parallel lines with different intercepts.</p>
              <strong>Question:</strong> How many solutions does this system have?
            </div>
          ),
          a: "No solutions",
          distract: ["Exactly one solution", "Infinitely many solutions"]
        })
      }
    ];
  
  
      /* -----------------------------------------
         CH 6: sequencesSeries
      ----------------------------------------- */
      case "sequencesSeries":
    return [
      // 1) Basic nth-term of Arithmetic
      {
        question: () => {
          const qHtml = `
            <p><strong>Recall:</strong> For an arithmetic sequence with first term 
            <em>a<sub>1</sub></em> and common difference <em>d</em>, the <em>n</em>-th term is 
            <code>a<sub>n</sub> = a<sub>1</sub> + (n − 1)·d</code>.</p>
            <p><strong>Question:</strong> If <em>a<sub>1</sub> = 2</em> and <em>d = 3</em>, 
            what is <em>a<sub>5</sub></em>?</p>
          `;
          // a5 = 2 + (5−1)*3 = 2 +12=14
          return {
            q: qHtml,
            a: "14",
            distract: ["16", "17"]
          };
        }
      },
  
      // 2) 5th Term of an Arithmetic (similar to your existing example)
      {
        question: () => {
          const qHtml = `
            <p><strong>Scenario:</strong> An arithmetic sequence starts at 4 with <em>d=5</em>. 
            <strong>Question:</strong> What is the 5th term?</p>
            <p>(Use <code>a<sub>n</sub> = a<sub>1</sub> + (n−1)d</code>.)</p>
          `;
          // terms: 4, 9, 14, 19, 24 => 5th is 24
          return {
            q: qHtml,
            a: "24",
            distract: ["20", "29"]
          };
        }
      },
  
      // 3) Sum of n terms of Arithmetic
      {
        question: () => {
          const qHtml = `
            <p><strong>Recall:</strong> Sum of first n terms of arithmetic sequence is 
            <code>S<sub>n</sub> = (n/2)[2a<sub>1</sub> + (n−1)d]</code>.</p>
            <p><strong>Scenario:</strong> a<sub>1</sub>=5, d=3, n=4. 
            <strong>Question:</strong> Find S<sub>4</sub>.</p>
          `;
          // S4 = (4/2)[2*5 + (4−1)*3] = 2[10 + 9]= 2*19=38
          return {
            q: qHtml,
            a: "38",
            distract: ["28", "34", "40"]
          };
        }
      },
  
      // 4) Missing Terms in Arithmetic 
      {
        question: () => {
          const qHtml = `
            <p><strong>Scenario:</strong> An arithmetic sequence has 4th term=14, 
            7th term=23. <strong>Question:</strong> What is the common difference d?</p>
            <p>(Hint: 
              <code>a<sub>7</sub> = a<sub>4</sub> + 3d</code>.)
            </p>
          `;
          // a4=14, a7=23 => 23=14+3d => d=3
          return {
            q: qHtml,
            a: "3",
            distract: ["2", "3.5", "4"]
          };
        }
      },
  
      // 5) Basic nth-term of Geometric
      {
        question: () => {
          const qHtml = `
            <p><strong>Recall:</strong> For a geometric sequence with first term 
            <em>a<sub>1</sub></em> and ratio <em>r</em>, the <em>n</em>-th term is 
            <code>a<sub>n</sub> = a<sub>1</sub> · r<sup>n−1</sup></code>.</p>
            <p><strong>Scenario:</strong> a<sub>1</sub>=3, r=2. 
            <strong>Question:</strong> The 4th term is?</p>
          `;
          // 3,6,12,24 => 4th is 24
          return {
            q: qHtml,
            a: "24",
            distract: ["6", "48"]
          };
        }
      },
  
      // 6) Another geometric nth-term 
      {
        question: () => {
          const qHtml = `
            <p><strong>Scenario:</strong> A geometric sequence has first term 10 
            and ratio 1/2. <strong>Question:</strong> The 5th term is?</p>
            <p>(Use <code>a<sub>n</sub>=a<sub>1</sub> · r<sup>n−1</sup></code>.)</p>
          `;
          // a1=10, r=0.5 => a5=10*(0.5)^4=10*(1/16)=0.625 
          // but to keep distractors simpler let's do 0.625 or 5 or 1.25
          return {
            q: qHtml,
            a: "0.625",
            distract: ["1.25", "5", "2.5"]
          };
        }
      },
  
      // 7) Sum of n terms of Geometric 
      {
        question: () => {
          const qHtml = `
            <p><strong>Recall:</strong> Sum of first n terms of geometric sequence 
            (<em>r≠1</em>) is <code>S<sub>n</sub> = a<sub>1</sub>(1−r<sup>n</sup>)/(1−r)</code>.</p>
            <p><strong>Scenario:</strong> a<sub>1</sub>=2, r=3, n=4. 
            <strong>Question:</strong> Find S<sub>4</sub>.</p>
          `;
          // terms: 2,6,18,54 => sum=80 
          // S4=2*(1−3^4)/(1−3)=2*(1−81)/(-2)=2*(−80)/(-2)=80
          return {
            q: qHtml,
            a: "80",
            distract: ["72", "54", "60"]
          };
        }
      },
  
      // 8) Identify Arithmetic vs. Geometric
      {
        question: () => {
          const qHtml = `
            <p><strong>Question:</strong> Which of these sequences is <em>geometric</em>?
            <br/>
            A) 3,7,11,15,... 
            <br/>
            B) 5,10,20,40,... 
            <br/>
            C) 2,4,7,10,...
            </p>
          `;
          // B) is geometric (r=2).
          return {
            q: qHtml,
            a: "B) 5,10,20,40,...",
            distract: ["A) 3,7,11,15,...", "C) 2,4,7,10,..."]
          };
        }
      },
  
      // 9) Condition for infinite geometric sum
      {
        question: () => {
          const qHtml = `
            <p>
              <strong>Recall:</strong> An infinite geometric series 
              <code>a<sub>1</sub> + a<sub>1</sub>r + a<sub>1</sub>r<sup>2</sup> + ...</code> 
              converges only if <code>|r| &lt; 1</code>.
              <br/>
              <strong>Question:</strong> For which ratio(s) below does the series converge?
            </p>
            <ul>
              <li>A) r=−1.2</li>
              <li>B) r=0.5</li>
              <li>C) r=1</li>
              <li>D) r=2</li>
            </ul>
          `;
          // Only r=0.5 => |r|<1 => B
          return {
            q: qHtml,
            a: "B) r=0.5",
            distract: ["A) r=−1.2", "C) r=1", "D) r=2"]
          };
        }
      },
  
      // 10) Another arithmetic partial sum 
      {
        question: () => {
          const qHtml = `
            <p><strong>Scenario:</strong> In an arithmetic sequence, a<sub>1</sub>=5, 
            a<sub>7</sub>=29. 
            <strong>Question:</strong> Find S<sub>7</sub>, the sum of the first 7 terms.
            <br/>
            (Hint: first find d, then use the sum formula.)
            </p>
          `;
          // a7=5+(7-1)d=5+6d => 29=5+6d => d=4 => sum(7)= (7/2)[2*5+(7-1)*4]
          // => (7/2)[10+24]=(7/2)*34=7*17=119
          return {
            q: qHtml,
            a: "119",
            distract: ["112", "125", "135"]
          };
        }
      }
    ];
  
  
      /* -----------------------------------------
         CH 7: financialLit
      ----------------------------------------- */
      case "financialLit":
    return [
      // 1) Basic Lump Sum - short term
      {
        question: () => {
          const qHtml = `
            <p><strong>Scenario:</strong>$1000 invested at 5% annually for 2 years 
            (compounded once per year).</p>
            <p><strong>Question:</strong> What is the future value after 2 years?</p>
          `;
          const fv = 1000 * Math.pow(1.05, 2); // 1102.50
          return {
            q: qHtml,
            a: `$${fv.toFixed(2)}`,
            distract: ["$1050.00", "$1150.00"]
          };
        }
      },
  
      // 2) Depreciation 1 Year
      {
        question: () => {
          const qHtml = `
            <p><strong>Scenario:</strong> A car depreciates by 10% each year from $20,000. 
            <br/><strong>Question:</strong> Value after 1 year?</p>
          `;
          const val = 20000 * 0.9; // 18000
          return {
            q: qHtml,
            a: `$${val.toFixed(0)}`,
            distract: ["$16,000", "$20,000"]
          };
        }
      },
  
      // 3) Lump Sum - bigger term
      {
        question: () => {
          const qHtml = `
            <p><strong>Scenario:</strong> You deposit $2,000 at 4% annual interest, 
            compounded yearly, for 5 years.</p>
            <p><strong>Question:</strong> Approximately how much will you have?</p>
          `;
          const fv = 2000 * Math.pow(1.04, 5); // 2000*(1.04^5) ~ 2000*1.21665=2433.3
          return {
            q: qHtml,
            a: `$${fv.toFixed(2)}`,
            distract: ["$2,320.00", "$2,700.00", "$3,200.00"]
          };
        }
      },
  
      // 4) Distinguish Simple vs Compound
      {
        question: () => {
          const qHtml = `
            <p><strong>Scenario:</strong> Compare simple interest vs. compound interest:
            <br/>- $1,000 at 5% <em>simple</em> for 3 years
            <br/>- $1,000 at 5% <em>compound</em> for 3 years
            </p>
            <p><strong>Question:</strong> Which is larger and by how much (approx)?</p>
          `;
          // simple => 1000 + 3*(1000*0.05)= 1150
          // compound => 1000*(1.05^3)= ~1157.63
          // difference => ~7.63
          return {
            q: qHtml,
            a: "Compound yields about $7.63 more",
            distract: [
              "They end up the same",
              "Simple is larger by $7.63",
              "Compound is larger by $50"
            ]
          };
        }
      },
  
      // 5) Monthly compounding vs annual
      {
        question: () => {
          const qHtml = `
            <p><strong>Scenario:</strong> $1,000 at 12% annual nominal rate, 
            but monthly compounding (i.e. 1% per month).
            <br/><strong>Question:</strong> After 1 year, the effective annual interest 
            is ~ what?</p>
            <p>(Hint: (1+0.01)^12 −1 = ? )</p>
          `;
          // (1+0.01)^12= ~1.126825 => 12.6825% effective
          const eff = (Math.pow(1.01, 12) - 1) * 100; 
          return {
            q: qHtml,
            a: `${eff.toFixed(2)}% (approx)`,
            distract: ["12.00%", "13.00%", "15.00%"]
          };
        }
      },
  
      // 6) Annuity (1 deposit/year)
      {
        // Optional illustration => <AnnuityDemo monthlyDeposit={200} monthlyRate={0.05} ... />
        // if you like
        question: () => {
          const qHtml = `
            <p><strong>Scenario:</strong> You deposit $500 each year into an account 
            at 5% annual. After 3 deposits, what's the total (annuity formula)?</p>
            <p><strong>Recall:</strong> S(n)=D[(1+r)^n −1]/r.</p>
          `;
          // D=500, r=0.05, n=3 => S3=500*((1.05^3)-1)/0.05 => 500*(1.157625 -1)/0.05=500*(0.157625/0.05)=500*3.1525=1576.25
          const total = 500 * ((Math.pow(1.05, 3) - 1) / 0.05);
          return {
            q: qHtml,
            a: `$${total.toFixed(2)}`,
            distract: ["$1500", "$1365.00", "$1800"]
          };
        }
      },
  
      // 7) More Depreciation - multiple years
      {
        question: () => {
          const qHtml = `
            <p><strong>Scenario:</strong> A machine costs $15,000 and depreciates 8% each year. 
            <br/><strong>Question:</strong> Its value after 3 years?</p>
          `;
          // initial=15000 => val= 15000*(1-0.08)^3=15000*(0.92^3)=15000*0.778688=11680.32
          const val = 15000 * Math.pow(0.92, 3);
          return {
            q: qHtml,
            a: `$${val.toFixed(2)}`,
            distract: ["$12,400.00", "$9,800.00", "$15,000.00"]
          };
        }
      },
  
      // 8) Breaking down interest vs. deposit
      {
        question: () => {
          const qHtml = `
            <p><strong>Scenario:</strong> In an annuity, you deposit $200 monthly 
            at 1% monthly interest. By the 6th deposit, the total is the sum of 6 
            deposits plus interest on earlier ones.</p>
            <p><strong>Question:</strong> Which part is typically largest at first, 
            the principal deposits or the interest gained?</p>
          `;
          return {
            q: qHtml,
            a: "The principal deposits overshadow interest in early months",
            distract: [
              "Interest always surpasses deposits from the start",
              "They are equal at first"
            ]
          };
        }
      },
  
      // 9) Mortgage interest concept
      {
        question: () => {
          const qHtml = `
            <p><strong>Scenario:</strong> A $300,000 mortgage at 3% annual, 
            amortized over 25 years, involves monthly payments. Part of each payment 
            covers interest, the rest reduces principal.</p>
            <p><strong>Question:</strong> Early in the mortgage, do you pay mostly 
            interest or mostly principal?</p>
          `;
          return {
            q: qHtml,
            a: "Mostly interest initially; principal portion grows over time",
            distract: [
              "Mostly principal at first",
              "It's an equal split from the start",
              "No difference, all goes to principal"
            ]
          };
        }
      },
  
      // 10) Compare 2 different compound frequencies
      {
        question: () => {
          const qHtml = `
            <p><strong>Scenario:</strong> Compare $1,000 at 8% annual, 
            vs. $1,000 at 8% annual but quarterly compounding.</p>
            <p><strong>Question:</strong> Which yields more after 1 year?</p>
          `;
          // annual => 1.08 => 1080
          // quarterly => (1+0.08/4)^4=1.02^4=1.082432 => 1082.43 => a bit more
          return {
            q: qHtml,
            a: "Quarterly compounding yields slightly more",
            distract: [
              "They are exactly the same",
              "Annual compounding yields more interest"
            ]
          };
        }
      }
    ];
  
  
      /* -----------------------------------------
         CH 8: statsProbability
      ----------------------------------------- */
      case "statsProbability":
    return [
      // 1 ─ Outlier effect
      {
        question: () => {
          const qHtml = `
            <p><strong>Scenario:</strong> A data set {3, 3, 4, 5, 100}. 
            <strong>Question:</strong> Which measure is most affected by the outlier&nbsp;100?</p>
          `;
          return {
            q: qHtml,
            a: "Mean",
            distract: ["Median", "Mode", "All equally affected"]
          };
        }
      },
  
      // 2 ─ Sum of two dice
      {
        question: () => {
          const qHtml = `
            <p><strong>Scenario:</strong> Rolling two fair dice. 
            <strong>Question:</strong> What is the probability the sum is&nbsp;7?</p>
          `;
          return { q: qHtml, a: "1/6", distract: ["1/12", "1/4"] };
        }
      },
  
      // 3 ─ No 6 in three rolls
      {
        question: () => {
          const qHtml = `
            <p><strong>Scenario:</strong> Rolling a fair die three times.
            <strong>Question:</strong> Probability of getting <em>no</em> sixes?</p>
          `;
          return { q: qHtml, a: "(5/6)<sup>3</sup> = 125/216", distract: ["1/6", "25/216"] };
        }
      },
  
      // 4 ─ Exactly 2 heads in 3 flips
      {
        question: () => {
          const qHtml = `
            <p><strong>Scenario:</strong> Flipping three fair coins.
            <strong>Question:</strong> Probability of exactly two heads?</p>
          `;
          return { q: qHtml, a: "3/8", distract: ["1/8", "1/4"] };
        }
      },
  
      // 5 ─ Mode of a small set
      {
        question: () => {
          const qHtml = `
            <p><strong>Scenario:</strong> Data set {2, 4, 4, 6, 8}.
            <strong>Question:</strong> What is the mode?</p>
          `;
          return { q: qHtml, a: "4", distract: ["2", "6"] };
        }
      },
  
      // 6 ─ Shape from mean/median/mode ordering
      {
        question: () => {
          const qHtml = `
            <p><strong>Scenario:</strong> A distribution has <em>mean</em> = 70, <em>median</em> = 75, <em>mode</em> = 80.
            <strong>Question:</strong> The distribution is most likely…</p>
          `;
          return { q: qHtml, a: "Left-skewed", distract: ["Right-skewed", "Symmetric"] };
        }
      },
  
      // 7 ─ Ace in a deck
      {
        question: () => {
          const qHtml = `
            <p><strong>Scenario:</strong> Drawing one card from a standard 52-card deck. 
            <strong>Question:</strong> Probability of drawing an Ace?</p>
          `;
          return { q: qHtml, a: "1/13", distract: ["1/4", "1/52"] };
        }
      },
  
      // 8 ─ Standard deviation of identical values
      {
        question: () => {
          const qHtml = `
            <p><strong>Scenario:</strong> A data set where every value equals 15.
            <strong>Question:</strong> What is its standard deviation?</p>
          `;
          return { q: qHtml, a: "0", distract: ["1", "Cannot be determined"] };
        }
      },
  
      // 9 ─ Mutually exclusive events
      {
        question: () => {
          const qHtml = `
            <p><strong>Scenario:</strong> Events A and B are mutually exclusive.
            <strong>Question:</strong> What is P(A&nbsp;and&nbsp;B)?</p>
          `;
          return { q: qHtml, a: "0", distract: ["P(A)+P(B)", "P(A)·P(B)"] };
        }
      },
  
      // 10 ─ Expected value of a binomial
      {
        question: () => {
          const qHtml = `
            <p><strong>Scenario:</strong> X&nbsp;~&nbsp;Binomial(n = 10, p = 0.5).
            <strong>Question:</strong> What is E[X] (the expected number of successes)?</p>
          `;
          return { q: qHtml, a: "5", distract: ["2.5", "10"] };
        }
      }
    ];
  
  
      /* -----------------------------------------
         CH 9: inequalities
      ----------------------------------------- */
      case "inequalities":
    return [
      {
        question: () => {
          const qHtml = `
            <p><strong>Scenario:</strong> Solve -2x + 5 > 1.</p>
          `;
          // -2x > 1 - 5 => -2x > -4 => x < 2
          return {
            q: qHtml,
            a: "x < 2",
            distract: ["x > 2", "x < -2"]
          };
        }
      },
      {
        question: () => {
          const qHtml = `
            <p><strong>Scenario:</strong> Graph y > 3x + 1. Which region is shaded?</p>
          `;
          return {
            q: qHtml,
            a: "Above the line y = 3x + 1",
            distract: ["Below that line", "On the line only"]
          };
        }
      },
  
      // 3rd question
      {
        question: () => {
          const qHtml = `
            <p><strong>Scenario:</strong> Solve 4 - x ≤ 7.</p>
          `;
          // 4 - x ≤ 7 => -x ≤ 3 => x ≥ -3
          return {
            q: qHtml,
            a: "x ≥ -3",
            distract: ["x ≤ -3", "All real numbers"]
          };
        }
      },
  
      // 4th question
      {
        question: () => {
          const qHtml = `
            <p><strong>Scenario:</strong> Graph y < -x + 5. Which region is shaded?</p>
          `;
          // The region below the line y = -x + 5
          return {
            q: qHtml,
            a: "Below the line y = -x + 5",
            distract: ["Above that line", "On the line only"]
          };
        }
      },
  
      // 5th question
      {
        question: () => {
          const qHtml = `
            <p><strong>Scenario:</strong> Graph x + y > 3. Which region is shaded?</p>
          `;
          // x + y > 3 => y > -x + 3 => region above the line
          return {
            q: qHtml,
            a: "Above the line y = -x + 3",
            distract: ["Below that line", "On the line only"]
          };
        }
      }
    ];
  
  
      /* -----------------------------------------
         CH 10: functionTransforms
      ----------------------------------------- */
      case "functionTransforms":
    return [
      {
        question: () => {
          const qHtml = `
            <p><strong>Scenario:</strong> If f(x)=x^2, then g(x)=f(x)-3. 
            <strong>Question:</strong> How is the graph of g related to f?</p>
          `;
          return {
            q: qHtml,
            a: "Shifted down 3 units",
            distract: ["Shifted up 3 units", "Reflected across x-axis"]
          };
        }
      },
      {
        question: () => {
          const qHtml = `
            <p><strong>Scenario:</strong> If h(x)=2*f(x), 
            <strong>Question:</strong> that's a vertical stretch by factor?</p>
          `;
          return {
            q: qHtml,
            a: "2",
            distract: ["1/2", "No stretch, just a shift"]
          };
        }
      },
  
      // 3rd question: horizontal shift
      {
        question: () => {
          const qHtml = `
            <p><strong>Scenario:</strong> If p(x)=f(x-2) where f(x)=x^2, 
            <strong>Question:</strong> how is p's graph related to f?</p>
          `;
          // f(x - 2) => shift right by 2
          return {
            q: qHtml,
            a: "Shifted right by 2 units",
            distract: ["Shifted left by 2 units", "Shifted up by 2 units"]
          };
        }
      },
  
      // 4th question: reflection across x-axis
      {
        question: () => {
          const qHtml = `
            <p><strong>Scenario:</strong> If q(x)=-f(x) where f(x)=x^2, 
            <strong>Question:</strong> what transformation is applied?</p>
          `;
          // -f(x) => reflection across x-axis
          return {
            q: qHtml,
            a: "Reflected across the x-axis",
            distract: ["Reflected across the y-axis", "Shifted down 1 unit"]
          };
        }
      },
  
      // 5th question: reflection across y-axis
      {
        question: () => {
          const qHtml = `
            <p><strong>Scenario:</strong> If r(x)=f(-x) where f(x)=x^2, 
            <strong>Question:</strong> what transformation is applied?</p>
          `;
          // f(-x) => reflection across y-axis
          return {
            q: qHtml,
            a: "Reflected across the y-axis",
            distract: ["Reflected across the x-axis", "Shifted left by 2 units"]
          };
        }
      }
    ];
  
  
      default:
        // no single topic => empty set
        return [];
    }
  }